/**
 * webauthn
 *
 * the browser half of WebAuthn as plain helper functions, deliberately without
 * any react or service dependency so both the login step and the self service
 * screen can use the very same conversion.
 *
 * the backend speaks json: every binary field arrives as a base64url string and
 * has to go back as a base64url string. navigator.credentials on the other hand
 * only understands ArrayBuffer. everything in here exists to bridge exactly that
 * gap - the conversion is written by hand instead of pulling in another package,
 * because it is a handful of lines and one more dependency in the login path is
 * the last thing this project needs.
 *
 * the native helpers PublicKeyCredential.parseCreationOptionsFromJSON,
 * parseRequestOptionsFromJSON and PublicKeyCredential.prototype.toJSON do the
 * same job, but they only exist from Chrome 129 / Firefox 119 / Safari 18.4 on.
 * they are used when the browser has them and skipped silently otherwise, and
 * both paths are normalized to the identical json shape so no caller ever has to
 * ask which one ran.
 */

/**
 * a credential the server already knows, as it travels over json
 */
export interface WebAuthnCredentialDescriptorJson {
  type?: string;
  id: string;
  transports?: string[];
}

/**
 * PublicKeyCredentialCreationOptions as json (registration)
 * the index signature is intentional: the php serializer adds fields (hints,
 * attestationFormats, ...) that are simply forwarded to the browser
 */
export interface WebAuthnCreationOptionsJson {
  challenge: string;
  rp?: { id?: string; name?: string };
  user: { id: string; name?: string; displayName?: string };
  pubKeyCredParams?: Array<{ type: string; alg: number }>;
  timeout?: number;
  excludeCredentials?: WebAuthnCredentialDescriptorJson[];
  authenticatorSelection?: { [key: string]: any };
  attestation?: string;
  extensions?: { [key: string]: any };
  [key: string]: any;
}

/**
 * PublicKeyCredentialRequestOptions as json (login / second factor)
 */
export interface WebAuthnRequestOptionsJson {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: WebAuthnCredentialDescriptorJson[];
  userVerification?: string;
  extensions?: { [key: string]: any };
  [key: string]: any;
}

/**
 * the answer of navigator.credentials.create, ready to be posted as json
 */
export interface WebAuthnAttestationCredentialJson {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment: string | null;
  clientExtensionResults: { [key: string]: any };
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports: string[];
  };
}

/**
 * the answer of navigator.credentials.get, ready to be posted as json
 */
export interface WebAuthnAssertionCredentialJson {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment: string | null;
  clientExtensionResults: { [key: string]: any };
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
}

/**
 * whatever the browser handed back, both cases travel to the backend the same way
 */
export type WebAuthnCredentialJson =
  WebAuthnAttestationCredentialJson | WebAuthnAssertionCredentialJson;

/**
 * base64UrlToArrayBuffer
 *
 * base64url differs from base64 in exactly three points and all three are
 * handled here: '-' stands for '+', '_' stands for '/' and the '=' padding is
 * dropped. atob() insists on the padding, so it is put back before decoding.
 * a length of n % 4 === 1 cannot be produced by any encoder and is rejected
 * instead of being padded into silent nonsense.
 *
 * @param value
 * @param field used for the error message only, never the value itself
 */
export function base64UrlToArrayBuffer(value: string, field: string = 'value'): ArrayBuffer {
  if (typeof value !== 'string') {
    throw new Error('WebAuthn: "' + field + '" is missing or not a string.');
  }

  /**
   * whitespace and line breaks are tolerated because php base64 helpers wrap
   * long strings often enough to matter
   */
  let normalized = value.replace(/\s+/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .replace(/=+$/, '');

  if (normalized.length % 4 === 1) {
    throw new Error('WebAuthn: "' + field + '" has an impossible base64url length.');
  }

  while (normalized.length % 4 !== 0) {
    normalized += '=';
  }

  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(normalized)) {
    throw new Error('WebAuthn: "' + field + '" is not valid base64url.');
  }

  const binary = window.atob(normalized),
    bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes.buffer as ArrayBuffer;
}

/**
 * arrayBufferToBase64Url
 *
 * the way back. the padding is stripped again and '+' / '/' are replaced,
 * because that is what the backend validates against.
 * the bytes are collected in a plain loop on purpose: String.fromCharCode.apply
 * on a large buffer blows the argument limit of the js engine.
 *
 * @param buffer
 */
export function arrayBufferToBase64Url(buffer: ArrayBuffer | null | undefined): string {
  if (!buffer) {
    return '';
  }

  const bytes = new Uint8Array(buffer);

  let binary = '';

  for (let index = 0; index < bytes.length; index++) {
    binary += String.fromCharCode(bytes[index]);
  }

  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * isWebAuthnSupported
 *
 * every branch is checked separately: an http origin other than localhost has
 * navigator.credentials but no PublicKeyCredential, and some embedded browsers
 * ship the object without the two methods we need.
 */
export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && !!window.navigator
    && !!window.navigator.credentials
    && typeof window.navigator.credentials.create === 'function'
    && typeof window.navigator.credentials.get === 'function';
}

/**
 * isConditionalMediationAvailable
 *
 * conditional mediation is what lets a passkey appear inside the browsers own
 * autofill dropdown instead of a modal. it is a separate capability from
 * WebAuthn itself and it must never make the login fail, so every negative
 * answer - missing method, rejected promise - simply means "no".
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false;
  }

  const publicKeyCredential: any = window.PublicKeyCredential;

  if (typeof publicKeyCredential.isConditionalMediationAvailable !== 'function') {
    return false;
  }

  try {
    return await publicKeyCredential.isConditionalMediationAvailable() === true;
  } catch (error) {
    return false;
  }
}

/**
 * createCredential
 *
 * registration: turns the json options into a real credential and gives back the
 * json the backend expects at /user/two-factor/passkeys.
 *
 * @param options PublicKeyCredentialCreationOptions as delivered by the backend
 */
export async function createCredential(
  options: WebAuthnCreationOptionsJson
): Promise<WebAuthnAttestationCredentialJson> {
  assertSupported();

  const publicKeyCredential: any = window.PublicKeyCredential,
    publicKey: PublicKeyCredentialCreationOptions =
      typeof publicKeyCredential.parseCreationOptionsFromJSON === 'function'
        ? publicKeyCredential.parseCreationOptionsFromJSON(options)
        : decodeCreationOptions(options);

  const credential = await window.navigator.credentials.create({ publicKey });

  if (!credential) {
    throw new Error('WebAuthn: the browser returned no credential.');
  }

  const native: any = credential;

  /**
   * toJSON() already produces base64url, it only has to be brought into the
   * exact same shape as the manual path
   */
  if (typeof native.toJSON === 'function') {
    return normalizeAttestation(native.toJSON());
  }

  const response = native.response as AuthenticatorAttestationResponse;

  return normalizeAttestation({
    id: native.id,
    rawId: arrayBufferToBase64Url(native.rawId),
    type: native.type,
    authenticatorAttachment: native.authenticatorAttachment,
    clientExtensionResults: typeof native.getClientExtensionResults === 'function'
      ? native.getClientExtensionResults()
      : {},
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(response.attestationObject),
      /**
       * getTransports() is older than toJSON() but still not everywhere, and the
       * backend treats the list as optional
       */
      transports: typeof (response as any).getTransports === 'function'
        ? (response as any).getTransports()
        : []
    }
  });
}

/**
 * getCredential
 *
 * login and second factor: signs the challenge and gives back the json the
 * verify endpoints expect.
 *
 * @param options PublicKeyCredentialRequestOptions as delivered by the backend
 * @param conditional true opens the credential inside the browsers autofill
 *        instead of a modal - only ever pass true after
 *        isConditionalMediationAvailable() said yes
 * @param signal ends the request in the browser, not only on our side. a
 *        conditional request stays open for the whole life of the page, and a
 *        browser refuses a second request while one is still pending
 *        (NotAllowedError, "a request is already pending") - which we would read
 *        as the user cancelling, so the explicit passkey button would appear to
 *        do nothing at all. only the signal really ends the silent one
 */
export async function getCredential(
  options: WebAuthnRequestOptionsJson,
  conditional: boolean = false,
  signal?: AbortSignal
): Promise<WebAuthnAssertionCredentialJson> {
  assertSupported();

  const publicKeyCredential: any = window.PublicKeyCredential,
    publicKey: PublicKeyCredentialRequestOptions =
      typeof publicKeyCredential.parseRequestOptionsFromJSON === 'function'
        ? publicKeyCredential.parseRequestOptionsFromJSON(options)
        : decodeRequestOptions(options);

  /**
   * 'conditional' is not part of the CredentialMediationRequirement union of the
   * typescript dom library shipped with this project, hence the any
   */
  const request: any = { publicKey };

  if (conditional) {
    request.mediation = 'conditional';
  }

  if (signal) {
    request.signal = signal;
  }

  const credential = await window.navigator.credentials.get(request);

  if (!credential) {
    throw new Error('WebAuthn: the browser returned no credential.');
  }

  const native: any = credential;

  if (typeof native.toJSON === 'function') {
    return normalizeAssertion(native.toJSON());
  }

  const response = native.response as AuthenticatorAssertionResponse;

  return normalizeAssertion({
    id: native.id,
    rawId: arrayBufferToBase64Url(native.rawId),
    type: native.type,
    authenticatorAttachment: native.authenticatorAttachment,
    clientExtensionResults: typeof native.getClientExtensionResults === 'function'
      ? native.getClientExtensionResults()
      : {},
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      /**
       * userHandle is null for a non discoverable credential and must stay null,
       * an empty string would be a valid handle to the backend
       */
      userHandle: response.userHandle ? arrayBufferToBase64Url(response.userHandle) : null
    }
  });
}

/**
 * assertSupported
 * one clear error instead of a TypeError deep inside the browser api
 */
function assertSupported(): void {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn: this browser or origin does not support WebAuthn.');
  }
}

/**
 * decodeCreationOptions
 * challenge, user.id and every excludeCredentials[].id become ArrayBuffer,
 * everything else is forwarded untouched
 * @param options
 */
function decodeCreationOptions(
  options: WebAuthnCreationOptionsJson
): PublicKeyCredentialCreationOptions {
  const source: any = options || {},
    user: any = source.user || {},
    result: any = {};

  /**
   * copied key by key instead of spreading, so an unexpected extra field cannot
   * overwrite one of the three decoded ones afterwards
   */
  Object.keys(source).forEach((key) => {
    result[key] = source[key];
  });

  result.challenge = base64UrlToArrayBuffer(source.challenge, 'challenge');

  const decodedUser: any = {};

  Object.keys(user).forEach((key) => {
    decodedUser[key] = user[key];
  });

  decodedUser.id = base64UrlToArrayBuffer(user.id, 'user.id');
  result.user = decodedUser;
  result.excludeCredentials = decodeDescriptors(source.excludeCredentials, 'excludeCredentials');

  return result as PublicKeyCredentialCreationOptions;
}

/**
 * decodeRequestOptions
 * challenge and every allowCredentials[].id become ArrayBuffer
 * @param options
 */
function decodeRequestOptions(
  options: WebAuthnRequestOptionsJson
): PublicKeyCredentialRequestOptions {
  const source: any = options || {},
    result: any = {};

  Object.keys(source).forEach((key) => {
    result[key] = source[key];
  });

  result.challenge = base64UrlToArrayBuffer(source.challenge, 'challenge');

  /**
   * an empty allowCredentials means "any discoverable credential" per spec, which
   * is exactly what the passwordless login needs - so the empty array is correct
   * and not a lost value
   */
  result.allowCredentials = decodeDescriptors(source.allowCredentials, 'allowCredentials');

  return result as PublicKeyCredentialRequestOptions;
}

/**
 * decodeDescriptors
 * @param descriptors
 * @param field
 */
function decodeDescriptors(
  descriptors: WebAuthnCredentialDescriptorJson[] | undefined,
  field: string
): PublicKeyCredentialDescriptor[] {
  if (!Array.isArray(descriptors)) {
    return [];
  }

  return descriptors
    .filter((descriptor) => !!(descriptor && descriptor.id))
    .map((descriptor, index) => {
      const result: any = {
        type: descriptor.type || 'public-key',
        id: base64UrlToArrayBuffer(descriptor.id, field + '[' + index + '].id')
      };

      if (Array.isArray(descriptor.transports) && descriptor.transports.length > 0) {
        result.transports = descriptor.transports;
      }

      return result as PublicKeyCredentialDescriptor;
    });
}

/**
 * normalizeAttestation
 * both the native toJSON path and the manual path end up here, so the caller
 * always sees the same keys and never an undefined
 * @param raw
 */
function normalizeAttestation(raw: any): WebAuthnAttestationCredentialJson {
  const source = raw || {},
    response = source.response || {};

  return {
    id: source.id || '',
    rawId: source.rawId || source.id || '',
    type: source.type || 'public-key',
    authenticatorAttachment: source.authenticatorAttachment || null,
    clientExtensionResults: source.clientExtensionResults || {},
    response: {
      clientDataJSON: response.clientDataJSON || '',
      attestationObject: response.attestationObject || '',
      transports: Array.isArray(response.transports) ? response.transports : []
    }
  };
}

/**
 * normalizeAssertion
 * @param raw
 */
function normalizeAssertion(raw: any): WebAuthnAssertionCredentialJson {
  const source = raw || {},
    response = source.response || {};

  return {
    id: source.id || '',
    rawId: source.rawId || source.id || '',
    type: source.type || 'public-key',
    authenticatorAttachment: source.authenticatorAttachment || null,
    clientExtensionResults: source.clientExtensionResults || {},
    response: {
      clientDataJSON: response.clientDataJSON || '',
      authenticatorData: response.authenticatorData || '',
      signature: response.signature || '',
      userHandle: response.userHandle || null
    }
  };
}
